/*
 * Where this site lives, and how to build an absolute URL against it.
 *
 * WHY IT IS ITS OWN MODULE. Two very different callers need the same answer:
 * plain node (the sitemap generator and the build-time head injector, which
 * require() this file directly) and the app bundle (views whose og:image and
 * og:url must be absolute -- crawlers and Twitter/X reject relative ones).
 * Keeping it out of src/data/publicRoutes.js means a view can import the URL
 * helpers without dragging the whole route table, and every blog post's title
 * and description, into the bundle it ships to readers.
 *
 * COMMONJS, deliberately, same reason the registry is: `npm run prebuild` and
 * the postbuild injector load this with node's own require(), and package.json
 * has no "type": "module". Webpack and vitest both import CommonJS from app
 * code without ceremony; node cannot import ESM from a CJS script.
 *
 * THE DEFAULT FAILS CLOSED TO PRODUCTION, and that is the whole point of the
 * file. og:url, canonical and og:image are baked into static HTML at build
 * time, so a build that guesses the origin wrong does not show a warning -- it
 * publishes pages whose canonical URL points at a host nobody is watching, and
 * Facebook, LinkedIn and Google cache that. `npm run build` with no env var set
 * is what a human runs by hand, so that case resolves to production and only an
 * explicit VUE_APP_FIREBASE_ENV=qa moves it.
 *
 * Note the inversion against src/firebase-config.js, which fails closed to QA:
 * there, the danger is prod credentials leaking into a QA build. Here the
 * danger is QA URLs leaking into prod pages. Same env var, opposite safe
 * default, so the two resolvers stay separate on purpose.
 *
 * VUE_APP_* vars are inlined by webpack at build time, so in the browser the
 * branch below is already decided and constant-folded; changing the variable at
 * runtime does nothing.
 */

const PROD_ORIGIN = "https://articulearnai.com";
// The QA Hosting site. Same origin cd.yml runs the QA e2e suite against.
const QA_ORIGIN = "https://articulearn-qa.web.app";

/**
 * The origin this build's pages will be served from.
 *
 * @param {string} [env] the VUE_APP_FIREBASE_ENV value; defaults to the
 *   process/bundle value. Passing it explicitly is what makes this testable
 *   without mutating the environment.
 * @returns {string} an origin with no trailing slash.
 */
function siteOrigin(env = process.env.VUE_APP_FIREBASE_ENV) {
  // Exact match, and only "qa". Anything else -- unset, misspelled, a stray
  // NODE_ENV-style value, a differently-cased "QA" -- is production.
  return env === "qa" ? QA_ORIGIN : PROD_ORIGIN;
}

/**
 * Join an origin and a root-relative path into an absolute URL.
 *
 * "/" is the reason this is a function rather than string concatenation: the
 * homepage has always been listed as "https://articulearnai.com" with no
 * trailing slash, and og:url / canonical must agree with the sitemap.
 */
function absoluteUrl(routePath, origin) {
  if (!origin) {
    throw new Error("absoluteUrl needs an origin");
  }
  if (!routePath.startsWith("/")) {
    throw new Error(`registry paths are root-relative, got: ${routePath}`);
  }
  // An origin read from an env var or a config file often arrives with a
  // trailing slash. Concatenating would give "https://host//about-us", which
  // is a different URL to every crawler and cache than the one in the sitemap.
  const base = origin.replace(/\/+$/, "");
  return routePath === "/" ? base : `${base}${routePath}`;
}

/** absoluteUrl against this build's own origin. What app code calls. */
function siteUrl(routePath, env) {
  return absoluteUrl(routePath, siteOrigin(env));
}

module.exports = {
  PROD_ORIGIN,
  QA_ORIGIN,
  siteOrigin,
  absoluteUrl,
  siteUrl,
};
